import React, { createContext, useState, useEffect } from "react";
import { msalInstance } from "../config/authConfig";
import { AccountInfo } from "@azure/msal-browser";

interface User {
  name: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Handle redirect response if present
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          setUser({
            name: response.account.name || "Unknown",
            email: response.account.username || "Unknown",
          });
          setIsAuthenticated(true);
        } else {
          // Check for an active account if no redirect response
          const account: AccountInfo | null = msalInstance.getActiveAccount();
          if (account) {
            setUser({
              name: account.name || "Unknown",
              email: account.username || "Unknown",
            });
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error("Error during authentication initialization:", error);
      }
    };

    initializeAuth();
  }, []);

  const login = () => {
    msalInstance.loginRedirect();
  };

  const logout = () => {
    msalInstance.logoutRedirect();
    setUser(null);
    setIsAuthenticated(false);
  };

  const contextValue = React.useMemo(
      () => ({ user, isAuthenticated, login, logout }),
      [user, isAuthenticated]
    );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
